import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { breadcrumbList } from "../seo/breadcrumbSchema";
import { jsonLdWithMedia } from "../seo/mediaSchema";
import { caseStudies } from "../data/caseStudies";
import { endUsers } from "../data/endUsers";

const manufacturerLogos = {
  ford: { src: "/ford2.png", width: 3840, height: 2160 },
  lucid: { src: "/vendor-partner-logos/lucid-logo.png", width: 400, height: 200 },
};

const studyColumns = [
  { key: "challenge", heading: "Challenge" },
  { key: "solution", heading: "What MAG did" },
  { key: "result", heading: "The result" },
];

export default function CaseStudies() {
  return (
    <div className="bg-white text-gray-900">
      <Seo
        title="AMR Case Studies from Live Automotive Plants | MAG"
        description="AMR and AGV integration work from live plants, including Ford and Lucid Motors programs. Phased rollouts, playbooks, and results."
        path="/case-studies"
        noindex
        jsonLd={jsonLdWithMedia(
          "/case-studies",
          breadcrumbList([
            { name: "Home", path: "/" },
            { name: "Case Studies", path: "/case-studies" },
          ])
        )}
      />

      <section className="px-4 pt-16 pb-10 sm:px-6 sm:pt-20 lg:px-8" aria-labelledby="case-studies-heading">
        <p className="page-eyebrow">Case Studies</p>
        <h1
          id="case-studies-heading"
          className="mt-3 max-w-3xl text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl lg:leading-[1.05]"
        >
          Work from live plants
        </h1>

        <h2
          id="named-work-heading"
          className="mt-8 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl"
        >
          Ford Motor Company and Lucid Motors
        </h2>
        <div className="mt-4 grid gap-2 lg:grid-cols-2">
          {endUsers.map((user) => (
            <div
              key={user.slug}
              className="flex items-center gap-5 bg-gray-100 px-5 py-4 sm:gap-6 sm:px-6"
            >
              <img
                src={manufacturerLogos[user.slug].src}
                alt={user.name}
                width={manufacturerLogos[user.slug].width}
                height={manufacturerLogos[user.slug].height}
                className={
                  user.slug === "lucid"
                    ? "h-[3.9rem] w-auto max-w-[182px] shrink-0 object-contain sm:h-[4.55rem] sm:max-w-[208px]"
                    : "h-12 w-auto max-w-[140px] shrink-0 object-contain sm:h-14 sm:max-w-[160px]"
                }
              />
              <p className="text-sm leading-relaxed text-gray-600 sm:text-base">
                {user.descriptor}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section
        className="border-t border-gray-200 px-4 py-10 sm:px-6 sm:py-12 lg:px-8"
        aria-labelledby="programs-heading"
      >
        <h2
          id="programs-heading"
          className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl"
        >
          Programs
        </h2>

        <div className="mt-8 space-y-10 sm:space-y-12">
          {caseStudies.map((study, index) => (
            <article key={study.id} id={study.id} className="scroll-mt-24">
              <div className="flex items-start gap-4 sm:gap-5">
                {study.logo ? (
                  <img
                    src={study.logo}
                    alt={study.plant}
                    width={study.logoWidth}
                    height={study.logoHeight}
                    className="h-16 w-auto max-w-[14rem] shrink-0 object-contain sm:h-20 sm:max-w-[16rem]"
                  />
                ) : null}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400">
                    {String(index + 1).padStart(2, "0")} · {study.plant}
                  </p>
                  <h3 className="mt-1.5 max-w-3xl text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                    {study.title}
                  </h3>
                  {study.summary ? (
                    <p className="mt-2 max-w-3xl text-base leading-relaxed text-gray-600">
                      {study.summary}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
                {studyColumns.map((column) => (
                  <div key={column.key} className="border-t border-gray-900 pt-3">
                    <h3 className="text-sm font-semibold text-gray-900">{column.heading}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600">
                      {study[column.key]}
                    </p>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-gray-950 px-4 py-12 text-white sm:px-6 sm:py-14 lg:px-8">
        <div className="absolute inset-0 bg-tech-grid opacity-30" aria-hidden="true" />
        <div className="relative">
          <h2 className="max-w-3xl text-2xl font-bold tracking-tight sm:text-3xl">
            Need the site-level detail?
          </h2>
          <p className="mt-3 max-w-xl text-base text-white/75 sm:text-lg">
            Under NDA, MAG shares scope, playbooks, and how a phased rollout would fit a specific floor.
          </p>
          <div className="mt-6">
            <Link to="/contact" className="btn-primary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
